import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // csx_user contains only display fields (name, email, role) — non-sensitive.
  // The session itself lives in the HttpOnly auth cookie set by the backend.
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("csx_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (email, password) => {
    // server sets HttpOnly auth cookie + readable csrf cookie
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("csx_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("[auth] logout failed:", err);
    }
    localStorage.removeItem("csx_user");
    setUser(null);
    window.location.href = "/login";
  }, []);

  useEffect(() => {
    // Validate session on mount using the HttpOnly cookie
    if (!user) {
      api.get("/auth/me").then(({ data }) => {
        setUser(data);
        localStorage.setItem("csx_user", JSON.stringify(data));
      }).catch((err) => {
        // 401 is expected when not logged in — interceptor already handles redirect
        if (err?.response?.status !== 401) {
          console.error("[auth] /auth/me failed:", err);
        }
      });
    }
  }, [user]);

  // Memoize the context value so consumers don't re-render on every parent render.
  const value = useMemo(
    () => ({ user, login, logout, loading, setLoading }),
    [user, login, logout, loading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
